import { existsSync, readFileSync, writeFileSync, mkdirSync } from 'node:fs';
import { dirname } from 'node:path';
import type { TlsOptions } from 'node:tls';
import { KEYUTIL, X509 } from 'jsrsasign';
import { mkcert } from '@interopio/gateway-server/tools';
import type { SslConfig } from '../../../types/config';
import getLogger from '../../logger.ts';

const logger = getLogger('ssl');

export type ServerCertificateConfig = {
    /** Hostname for the server certificate (used in CN and SAN) */
    host: string;
    /** Path to CA private key file (PEM format), for auto-generating server certificates */
    key?: string;
    /** Passphrase for encrypted CA private key */
    passphrase?: string;
};

export function secureContextOptions(ssl: SslConfig, serverCertificate?: ServerCertificateConfig): TlsOptions {
    // Prepare common SSL/TLS options (client certificate verification)
    const commonOptions: TlsOptions = {};

    if (ssl.requestCert !== undefined) {
        commonOptions.requestCert = ssl.requestCert;
    }
    if (ssl.rejectUnauthorized !== undefined) {
        commonOptions.rejectUnauthorized = ssl.rejectUnauthorized;
    }

    // Mode 1: Explicit key and cert provided - check if files exist and use them
    if (ssl.key && ssl.cert && existsSync(ssl.key) && existsSync(ssl.cert)) {
        logger.info(`using SSL/TLS certificate ${ssl.cert} with private key in ${ssl.key}${ssl.passphrase ? ' (password-protected)' : ''}`);
        const options: TlsOptions = {
            key: readFileSync(ssl.key),
            cert: readFileSync(ssl.cert),
            ...commonOptions
        };
        if (ssl.passphrase) {
            options.passphrase = ssl.passphrase;
        }
        // Only set ca if requestCert is true (ca is for validating client certificates)
        if (ssl.requestCert && ssl.ca && existsSync(ssl.ca)) {
            options.ca = readFileSync(ssl.ca);
        }
        return options;
    }

    // Mode 1b: Key and cert not explicitly provided, but default files exist - use them
    if (!ssl.key && !ssl.cert) {
        const defaultKeyPath = './gateway-server.key';
        const defaultCertPath = './gateway-server.crt';

        if (existsSync(defaultKeyPath) && existsSync(defaultCertPath)) {
            logger.info(`using SSL/TLS certificate ${defaultCertPath} with private key in ${defaultKeyPath}${ssl.passphrase ? ' (password-protected)' : ''}`);
            const options: TlsOptions = {
                key: readFileSync(defaultKeyPath),
                cert: readFileSync(defaultCertPath),
                ...commonOptions
            };
            if (ssl.passphrase) {
                options.passphrase = ssl.passphrase;
            }
            // Only set ca if requestCert is true (ca is for validating client certificates)
            if (ssl.requestCert && ssl.ca && existsSync(ssl.ca)) {
                options.ca = readFileSync(ssl.ca);
            }
            return options;
        }
    }

    // Mode 2: Generate server certificate using CA key (from auth.x509.key)
    // If serverCertificate is undefined, skip certificate generation
    if (!serverCertificate) {
        throw new Error('SSL/TLS enabled but no server certificate provided. Either provide ssl.key and ssl.cert, or configure auth.x509.key for auto-generation.');
    }

    // Note: CA key is primarily for client certificate generation, incidentally used for server cert
    const caKeyPath = serverCertificate.key ?? 'gateway-ca.key';
    const caPemPath = ssl.ca ?? `${caKeyPath.replace(/\.key$/, '.crt')}`;
    const passphrase = serverCertificate.passphrase ?? ssl.passphrase;

    // Auto-generate Root CA if it doesn't exist

    if (!existsSync(caKeyPath)) {
        if (existsSync(caPemPath)) {
            throw new Error(`CA key file not found: ${caKeyPath} (CA certificate exists: ${caPemPath})`);
        }
        const rootCA = mkcert.generateRootCA({ name: mkcert.DEFAULT_CA_NAME, passphrase });

        // Ensure directory exists
        const keyDir = dirname(caKeyPath);
        if (keyDir && keyDir !== '.' && !existsSync(keyDir)) {
            mkdirSync(keyDir, { recursive: true });
        }
        const certDir = dirname(caPemPath);
        if (certDir && certDir !== '.' && certDir !== keyDir && !existsSync(certDir)) {
            mkdirSync(certDir, { recursive: true });

        }
        writeFileSync(caKeyPath, rootCA.key, { mode: 0o400 });
        writeFileSync(caPemPath, rootCA.cert, { mode: 0o644 });
        logger.info(`created new local Root CA in ${caPemPath}, ${caKeyPath}${passphrase ? ' (password-protected)' : ''}`);
    }

    // Load CA key (with optional passphrase)
    const caKeyPem = readFileSync(caKeyPath, 'utf8');
    const caKeyObj = KEYUTIL.getKey(caKeyPem, passphrase);

    // Extract issuer from CA certificate
    const caCertPem = readFileSync(caPemPath, 'utf8');
    const caCert = new X509();
    caCert.readCertPEM(caCertPem);
    const issuer = caCert.getSubjectString();

    // Use hostname from serverCertificate config
    const hostname = serverCertificate.host;

    // Generate server certificate with correct issuer and hostname
    logger.debug(`generating server certificate signed by: ${issuer} for host: ${hostname}`);
    const serverCert = mkcert.generateCert(caKeyObj, issuer, [hostname], false); // false = server cert

    // Only save to disk if key or cert paths are explicitly provided
    if (ssl.key || ssl.cert) {
        const keyPath = ssl.key || './gateway-server.key';
        const certPath = ssl.cert || './gateway-server.crt';

        // Ensure directory exists for key
        const keyDir = dirname(keyPath);
        if (keyDir && keyDir !== '.' && !existsSync(keyDir)) {
            mkdirSync(keyDir, { recursive: true });
        }
        // Ensure directory exists for cert
        const certDir = dirname(certPath);
        if (certDir && certDir !== '.' && certDir !== keyDir && !existsSync(certDir)) {
            mkdirSync(certDir, { recursive: true });
        }

        writeFileSync(keyPath, serverCert.key, { mode: 0o600 });
        writeFileSync(certPath, serverCert.cert, { mode: 0o644 });

        logger.info(`generated server certificate saved to ${certPath} with private key in ${keyPath}${passphrase ? ' (password-protected)' : ''}`);
    } else {
        logger.info(`using in-memory server certificate for host: ${hostname}`);
    }
    const result: TlsOptions = {
        key: serverCert.key,
        cert: serverCert.cert,
        ...commonOptions
    };

    // Only set ca if requestCert is true (ca is for validating client certificates)
    // At this point, CA file is guaranteed to exist (either pre-existing or just generated)
    if (ssl.requestCert && ssl.ca && existsSync(caPemPath)) {
        result.ca = readFileSync(caPemPath);
    }

    return result;
}
